import { useMemo, useState } from 'react';
import { useAuth } from '../auth/AuthProvider';
import {
  useActualizarCorrelativo,
  useActualizarMapeo,
  useAnularAsiento,
  useAsientoDetalle,
  useAsientos,
  useBalance,
  useCerrarPeriodo,
  useCorrelativos,
  useCrearAsiento,
  useCrearCuentaContable,
  useCuentasContables,
  useMapeos,
  useMayor,
  usePeriodos,
} from '../api/queries-finanzas';
import { FinanceDemoNotice, TabBar } from '../components/FinanceBits';
import { Modal } from '../components/Modal';
import { MotivoModal } from '../components/MotivoModal';
import { ConfirmModal } from '../components/ConfirmModal';
import { SearchSelect } from '../components/SearchSelect';
import { StatusBadge } from '../components/Badges';
import { PlusIcon, TrashIcon } from '../components/icons';
import { useToast } from '../ui/ToastProvider';
import { formatDate, formatMoney, formatRate, todayIso } from '../lib/format';
import type { AsientoDetalle, CuentaContable } from '../api/types';

type Tab = 'balance' | 'asientos' | 'cuentas' | 'mayor' | 'numeracion' | 'configuracion';

const PREFIJOS_TALONARIO = [
  { id: 'FACTURA', label: 'Factura de venta', prefijo: '00' },
  { id: 'NOTA_CREDITO', label: 'Nota de crédito', prefijo: '01' },
  { id: 'NOTA_DEBITO', label: 'Nota de débito', prefijo: '02' },
  { id: 'ISLR', label: 'Comprobante ISLR', prefijo: 'ISLR' },
] as const;

interface LineaForm {
  cuentaId: string;
  descripcion: string;
  debe: string;
  haber: string;
}

const EMPTY_LINE: LineaForm = { cuentaId: '', descripcion: '', debe: '', haber: '' };
const today = todayIso();

function controlFormateado(prefijo: string, numero: number): string {
  return `${prefijo}-${String(numero).padStart(5, '0')}`;
}

export function ContabilidadPage() {
  const { user, demo } = useAuth();
  const { notify } = useToast();
  const [tab, setTab] = useState<Tab>('balance');
  const [desde, setDesde] = useState('');
  const [hasta, setHasta] = useState('');
  const [selectedAsiento, setSelectedAsiento] = useState<string | null>(null);
  const [openAsiento, setOpenAsiento] = useState(false);
  const [openCuenta, setOpenCuenta] = useState(false);
  const [mayorCuenta, setMayorCuenta] = useState('');
  const [cuentaForm, setCuentaForm] = useState({ codigo: '', nombre: '', tipo: 'ACTIVO', esDetalle: true });
  const [mapeoForm, setMapeoForm] = useState<Record<string, string>>({});
  const [siguienteControl, setSiguienteControl] = useState('');
  const [claveAbierta, setClaveAbierta] = useState(false);
  const [claveAdmin, setClaveAdmin] = useState('');
  const [claveError, setClaveError] = useState<string | null>(null);

  const balance = useBalance(desde || undefined, hasta || undefined);
  const asientos = useAsientos({ desde: desde || undefined, hasta: hasta || undefined, limit: 100 });
  const detalle = useAsientoDetalle(selectedAsiento);
  const cuentas = useCuentasContables();
  const mayor = useMayor(mayorCuenta || undefined, desde || undefined, hasta || undefined);
  const periodos = usePeriodos();
  const mapeos = useMapeos();
  const correlativos = useCorrelativos();
  const crearAsiento = useCrearAsiento();
  const anularAsiento = useAnularAsiento();
  const [annulAsiento, setAnnulAsiento] = useState<{ id: string; numero: number } | null>(null);
  const [periodoACerrar, setPeriodoACerrar] = useState<{ id: string; nombre: string } | null>(null);
  const crearCuenta = useCrearCuentaContable();
  const cerrarPeriodo = useCerrarPeriodo();
  const actualizarMapeo = useActualizarMapeo();
  const actualizarCorrelativo = useActualizarCorrelativo();
  const canEdit = !demo && user !== null && ['admin', 'supervisor'].includes(user.rol);
  const canEditNumbering = !demo && user?.rol === 'admin';
  const detailAccounts = (cuentas.data ?? []).filter((cuenta) => cuenta.esDetalle);
  const nivelCuenta = cuentaForm.codigo.trim().split('.').filter(Boolean).length;
  const talonario = (correlativos.data ?? []).find(
    (correlativo) => correlativo.tipo === 'CONTROL_TALONARIO',
  );
  const previewNumero = Number(siguienteControl || talonario?.siguienteNumero || 1);

  const abrirCambioControl = () => {
    const siguienteNumero = Number(siguienteControl || talonario?.siguienteNumero || 0);
    if (!Number.isInteger(siguienteNumero) || siguienteNumero < 1) {
      notify('Escribe un número inicial válido.', 'warning');
      return;
    }
    setSiguienteControl(String(siguienteNumero));
    setClaveAdmin('');
    setClaveError(null);
    setClaveAbierta(true);
  };

  const confirmarCambioControl = async () => {
    const siguienteNumero = Number(siguienteControl);
    if (!claveAdmin) {
      setClaveError('Ingresa la clave de administrador.');
      return;
    }
    try {
      await actualizarCorrelativo.mutateAsync({ siguienteNumero, password: claveAdmin });
      notify('Numeración del talonario actualizada.', 'success');
      setClaveAbierta(false);
      setClaveAdmin('');
      setClaveError(null);
    } catch (error) {
      setClaveError(error instanceof Error ? error.message : 'No se pudo actualizar la numeración.');
    }
  };

  const submitCuenta = async () => {
    if (!cuentaForm.codigo.trim() || !cuentaForm.nombre.trim()) {
      notify('Código y nombre son obligatorios.', 'warning');
      return;
    }
    if (cuentaForm.esDetalle !== (nivelCuenta === 4)) {
      notify('Solo las cuentas de cuarto nivel reciben movimientos; los niveles 1 a 3 son títulos.', 'warning');
      return;
    }
    if (nivelCuenta === 4) {
      const padre = cuentaForm.codigo.trim().split('.').slice(0, -1).join('.');
      if (!(cuentas.data ?? []).some((cuenta) => cuenta.codigo === padre && !cuenta.esDetalle)) {
        notify(`Crea primero la cuenta título ${padre}.`, 'warning');
        return;
      }
    }
    try {
      await crearCuenta.mutateAsync({
        codigo: cuentaForm.codigo.trim(),
        nombre: cuentaForm.nombre.trim(),
        tipo: cuentaForm.tipo,
        esDetalle: nivelCuenta === 4,
      });
      notify('Cuenta contable creada.', 'success');
      setOpenCuenta(false);
      setCuentaForm({ codigo: '', nombre: '', tipo: 'ACTIVO', esDetalle: true });
    } catch (error) {
      notify(error instanceof Error ? error.message : 'No se pudo crear la cuenta.', 'error');
    }
  };

  const annul = (asientoId: string, numero: number) =>
    setAnnulAsiento({ id: asientoId, numero });

  const confirmarAnnul = async (motivo: string) => {
    if (!annulAsiento) return;
    await anularAsiento.mutateAsync({ id: annulAsiento.id, motivo });
    notify(`Asiento #${annulAsiento.numero} anulado con reverso.`, 'success');
    setAnnulAsiento(null);
    setSelectedAsiento(null);
  };

  return (
    <div className="dashboard">
      <header className="page-head">
        <div>
          <p className="eyebrow">Contabilidad</p>
          <h1 className="page-title">Contabilidad & Partida Doble</h1>
          <p className="page-sub">Balance de comprobación, libro mayor, asientos y cuentas en bolívares, con respaldo en USD.</p>
        </div>
        <div className="page-head__actions">
          <button type="button" className="btn btn--outline" disabled={!canEdit} onClick={() => setOpenCuenta(true)}>
            Nueva cuenta
          </button>
          <button type="button" className="btn btn--primary" disabled={!canEdit} onClick={() => setOpenAsiento(true)}>
            <PlusIcon size={17} /> Asiento manual
          </button>
        </div>
      </header>

      <FinanceDemoNotice demo={demo} />

      <div className="page-tabs-row">
        <TabBar
          label="Secciones contables"
          value={tab}
          onChange={setTab}
          tabs={[
            { value: 'balance', label: 'Balance de comprobación' },
            { value: 'asientos', label: 'Asientos' },
            { value: 'cuentas', label: 'Plan de cuentas' },
            { value: 'mayor', label: 'Libro mayor' },
            { value: 'numeracion', label: 'Numeración fiscal' },
            { value: 'configuracion', label: 'Configuración' },
          ]}
        />
      </div>

      {tab === 'balance' ? (
        <section className="card transactions-card">
          <div className="transactions-filters">
            <div className="filter-tabs"><button type="button" className="is-active">Rango de fechas</button></div>
            <div className="filter-controls">
              <input type="date" className="input input--date" value={desde} onChange={(e) => setDesde(e.target.value)} aria-label="Desde" />
              <input type="date" className="input input--date" value={hasta} onChange={(e) => setHasta(e.target.value)} aria-label="Hasta" />
              {balance.data ? (
                <span className={`pill ${balance.data.totales.cuadrado ? 'pill--soft' : 'pill--warning'}`}>
                  {balance.data.totales.cuadrado ? 'Cuadrado' : 'Descuadrado'}
                </span>
              ) : null}
            </div>
          </div>
          <div className="table-scroll">
            <table className="data-table">
              <thead><tr><th>Código</th><th>Cuenta</th><th>Tipo</th><th className="align-right">Débito Bs.</th><th className="align-right">Crédito Bs.</th><th className="align-right">Saldo Bs.</th></tr></thead>
              <tbody>
                {balance.isLoading ? <tr><td colSpan={6}><div className="table-empty">Calculando balance…</div></td></tr> : null}
                {balance.data?.data.map((row) => (
                  <tr key={row.id}>
                    <td>{row.codigo}</td>
                    <td>{row.nombre}</td>
                    <td><span className="pill pill--soft">{row.tipo}</span></td>
                    <td className="align-right">{formatMoney(row.debitoVes, 'VES')}</td>
                    <td className="align-right">{formatMoney(row.creditoVes, 'VES')}</td>
                    <td className="align-right"><strong>{formatMoney(row.saldoVes, 'VES')}</strong></td>
                  </tr>
                ))}
              </tbody>
              {balance.data ? (
                <tfoot>
                  <tr>
                    <td colSpan={3}><strong>Totales</strong></td>
                    <td className="align-right"><strong>{formatMoney(balance.data.totales.debeVes, 'VES')}</strong></td>
                    <td className="align-right"><strong>{formatMoney(balance.data.totales.haberVes, 'VES')}</strong></td>
                    <td className="align-right">
                      {balance.data.totales.cuadrado ? 'Debe = Haber ✓' : 'Revisar asientos'}
                    </td>
                  </tr>
                </tfoot>
              ) : null}
            </table>
          </div>
        </section>
      ) : null}

      {tab === 'asientos' ? (
        <section className="card transactions-card">
          <div className="transactions-filters">
            <div className="filter-tabs"><button type="button" className="is-active">Últimos 100 asientos</button></div>
            <div className="filter-controls">
              <input type="date" className="input input--date" value={desde} onChange={(e) => setDesde(e.target.value)} aria-label="Desde" />
              <input type="date" className="input input--date" value={hasta} onChange={(e) => setHasta(e.target.value)} aria-label="Hasta" />
            </div>
          </div>
          <div className="table-scroll">
            <table className="data-table">
              <thead><tr><th>#</th><th>Fecha</th><th>Descripción</th><th>Origen</th><th>Período</th><th className="align-right">Debe Bs.</th><th className="align-right">Haber Bs.</th><th>Estado</th><th /></tr></thead>
              <tbody>
                {asientos.isLoading ? <tr><td colSpan={9}><div className="table-empty">Cargando asientos…</div></td></tr> : null}
                {!asientos.isLoading && (asientos.data?.data ?? []).length === 0 ? <tr><td colSpan={9}><div className="table-empty">Sin asientos en el rango.</div></td></tr> : null}
                {(asientos.data?.data ?? []).map((asiento) => (
                  <tr key={asiento.id}>
                    <td><strong>{asiento.numero}</strong></td>
                    <td>{formatDate(asiento.fecha)}</td>
                    <td>{asiento.descripcion}</td>
                    <td><span className="pill pill--soft">{asiento.origen}</span></td>
                    <td>{asiento.periodoNombre}</td>
                    <td className="align-right">{formatMoney(asiento.totalDebeVes, 'VES')}</td>
                    <td className="align-right">{formatMoney(asiento.totalHaberVes, 'VES')}</td>
                    <td><StatusBadge estado={asiento.estado} /></td>
                    <td>
                      <button type="button" className="btn btn--outline btn--sm" onClick={() => setSelectedAsiento(asiento.id)}>Ver</button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </section>
      ) : null}

      {tab === 'cuentas' ? (
        <section className="card transactions-card">
          <div className="clients-toolbar">
            <span className="pill pill--soft">{(cuentas.data ?? []).length} cuentas</span>
            <span className="pill pill--soft">{detailAccounts.length} reciben movimiento</span>
          </div>
          <p className="inline-note chart-note">
            Estructura: <strong>1</strong> → <strong>1.1</strong> → <strong>1.1.01</strong> son títulos.
            Solo <strong>1.1.01.001</strong> recibe asientos. Los títulos agrupan saldos y no se pueden
            usar para contabilizar.
          </p>
          <div className="table-scroll">
            <table className="data-table">
              <thead><tr><th>Código</th><th>Nombre</th><th>Tipo</th><th>Movimiento</th><th>Moneda</th><th>Estado</th></tr></thead>
              <tbody>
                {(cuentas.data ?? []).map((cuenta) => (
                  <tr key={cuenta.id}>
                    <td><strong>{cuenta.codigo}</strong></td>
                    <td>{cuenta.nombre}</td>
                    <td>{cuenta.tipo}</td>
                    <td>
                      <span className={`status-badge status-badge--${cuenta.esDetalle ? 'success' : 'neutral'}`}>
                        {cuenta.esDetalle ? 'Recibe' : 'Título'}
                      </span>
                    </td>
                    <td>{cuenta.moneda}</td>
                    <td><span className={`status-badge status-badge--${cuenta.activo ? 'success' : 'neutral'}`}>{cuenta.activo ? 'Activa' : 'Inactiva'}</span></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </section>
      ) : null}

      {tab === 'mayor' ? (
        <section className="card transactions-card">
          <div className="transactions-filters">
            <div className="filter-controls">
              <SearchSelect
                value={mayorCuenta}
                onChange={setMayorCuenta}
                options={detailAccounts.map((cuenta) => ({
                  value: cuenta.id,
                  label: cuenta.nombre,
                  hint: cuenta.codigo,
                }))}
                placeholder="Buscar cuenta contable"
                ariaLabel="Cuenta contable"
              />
              <input type="date" className="input input--date" value={desde} onChange={(e) => setDesde(e.target.value)} aria-label="Desde" />
              <input type="date" className="input input--date" value={hasta} onChange={(e) => setHasta(e.target.value)} aria-label="Hasta" />
            </div>
          </div>
          {mayor.data ? (
            <div className="table-scroll">
              <div className="ledger-head">
                <div><span>Cuenta</span><strong>{mayor.data.cuenta.codigo} · {mayor.data.cuenta.nombre}</strong></div>
                <div><span>Saldo inicial</span><strong>{formatMoney(mayor.data.saldoInicialUsd, 'USD')}</strong></div>
                <div><span>Saldo final</span><strong>{formatMoney(mayor.data.saldoFinalVes, 'VES')}</strong></div>
              </div>
              <table className="data-table data-table--compact">
                <thead><tr><th>#</th><th>Fecha</th><th>Asiento / línea</th><th className="align-right">Debe</th><th className="align-right">Haber</th><th className="align-right">Saldo</th></tr></thead>
                <tbody>
                  {mayor.data.movimientos.length === 0 ? <tr><td colSpan={6}><div className="table-empty">Sin movimientos en el rango.</div></td></tr> : null}
                  {mayor.data.movimientos.map((movement) => (
                    <tr key={`${movement.asientoId}-${movement.numero}-${movement.debeVes}-${movement.haberVes}`}>
                      <td>{movement.numero}</td>
                      <td>{formatDate(movement.fecha)}</td>
                      <td>
                        <div className="doc-cell"><strong>{movement.asientoDescripcion}</strong><span>{movement.descripcion}</span></div>
                      </td>
                      <td className="align-right">{formatMoney(movement.debeVes, 'VES')}</td>
                      <td className="align-right">{formatMoney(movement.haberVes, 'VES')}</td>
                      <td className="align-right"><strong>{formatMoney(movement.saldoVes, 'VES')}</strong></td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : <p className="muted" style={{ padding: 20 }}>Selecciona una cuenta para consultar su libro mayor.</p>}
        </section>
      ) : null}

      {tab === 'numeracion' ? (
        <div className="config-grid numbering-page">
          <section className="card config-card config-card--wide numbering-card">
            <div className="numbering-hero">
              <div>
                <span className="pill pill--soft">Talonario único</span>
                <h2 className="card-title">Un solo consecutivo para todo el talonario</h2>
                <p className="card-sub">
                  Cada factura, nota de crédito, nota de débito o comprobante ISLR consume el
                  siguiente número. El prefijo identifica el documento, pero la secuencia avanza
                  para todos.
                </p>
              </div>
              <div className="numbering-next">
                <span>Próximo control</span>
                <strong>{controlFormateado('00', previewNumero)}</strong>
                <small>Último usado: {talonario?.ultimoUtilizado || '—'}</small>
              </div>
            </div>

            <div className="numbering-previews">
              {PREFIJOS_TALONARIO.map((item) => (
                <div className="numbering-preview" key={item.id}>
                  <span>{item.label}</span>
                  <strong>{controlFormateado(item.prefijo, previewNumero)}</strong>
                  <small>Serie {item.prefijo}</small>
                </div>
              ))}
            </div>

            <div className="numbering-edit">
              <label className="field">
                <span className="field-label">Iniciar el talonario desde</span>
                <input
                  className="input"
                  type="number"
                  min="1"
                  value={siguienteControl || String(talonario?.siguienteNumero ?? '')}
                  disabled={!canEditNumbering}
                  onChange={(event) => setSiguienteControl(event.target.value)}
                />
              </label>
              <button
                type="button"
                className="btn btn--primary"
                disabled={!canEditNumbering || actualizarCorrelativo.isPending}
                onClick={abrirCambioControl}
              >
                Cambiar numeración
              </button>
            </div>

            <p className="page-footnote">
              {canEditNumbering
                ? 'Cambiar la numeración requiere la clave de un administrador. No se puede retroceder por debajo del último control usado.'
                : 'Solo un administrador puede cambiar la numeración del talonario.'}
            </p>
          </section>
        </div>
      ) : null}

      {tab === 'configuracion' ? (
        <div className="config-grid">
          <section className="card config-card">
            <div className="card-head"><div><h2 className="card-title">Períodos fiscales</h2><p className="card-sub">Cierra un período para bloquear nuevos asientos.</p></div></div>
            <ul className="simple-list">
              {(periodos.data ?? []).map((periodo) => (
                <li key={periodo.id}>
                  <span>{periodo.nombre} · {periodo.inicio} → {periodo.fin}</span>
                  <span className={`status-badge status-badge--${periodo.estado === 'ABIERTO' ? 'success' : 'neutral'}`}>{periodo.estado}</span>
                  {periodo.estado === 'ABIERTO' && canEdit ? (
                    <button
                      type="button"
                      className="text-link"
                      onClick={() => setPeriodoACerrar({ id: periodo.id, nombre: periodo.nombre })}
                    >
                      Cerrar
                    </button>
                  ) : null}
                </li>
              ))}
            </ul>
          </section>

          <section className="card config-card config-card--wide">
            <div className="card-head"><div><h2 className="card-title">Mapeos contables</h2><p className="card-sub">Cuenta usada por cada operación automática.</p></div></div>
            <div className="mapping-list">
              {(mapeos.data ?? []).map((mapeo) => (
                <div className="mapping-row" key={mapeo.id}>
                  <div><strong>{mapeo.clave}</strong><span>{mapeo.descripcion}</span></div>
                  <SearchSelect
                    value={mapeoForm[mapeo.clave] ?? mapeo.cuentaId}
                    disabled={!canEdit}
                    onChange={(value) => setMapeoForm((current) => ({ ...current, [mapeo.clave]: value }))}
                    options={detailAccounts.map((cuenta) => ({
                      value: cuenta.id,
                      label: cuenta.nombre,
                      hint: cuenta.codigo,
                    }))}
                    placeholder="Buscar cuenta"
                    ariaLabel={`Cuenta para ${mapeo.clave}`}
                  />
                  {canEdit ? (
                    <button
                      type="button"
                      className="btn btn--outline btn--sm"
                      onClick={async () => {
                        const cuentaId = mapeoForm[mapeo.clave] ?? mapeo.cuentaId;
                        try {
                          await actualizarMapeo.mutateAsync({ clave: mapeo.clave, cuentaId });
                          notify(`Mapeo ${mapeo.clave} actualizado.`, 'success');
                        } catch (error) {
                          notify(error instanceof Error ? error.message : 'No se pudo actualizar.', 'error');
                        }
                      }}
                    >
                      Guardar
                    </button>
                  ) : <span className="pill pill--soft">{mapeo.cuentaCodigo}</span>}
                </div>
              ))}
            </div>
          </section>
        </div>
      ) : null}

      {openAsiento ? (
        <AsientoForm
          cuentas={detailAccounts}
          onClose={() => setOpenAsiento(false)}
          onCreated={(numero) => {
            setOpenAsiento(false);
            notify(`Asiento #${numero} contabilizado.`, 'success');
          }}
        />
      ) : null}

      {openCuenta ? (
        <Modal title="Nueva cuenta contable" eyebrow="Plan de cuentas" onClose={() => setOpenCuenta(false)} footer={
          <><button type="button" className="btn btn--outline" onClick={() => setOpenCuenta(false)}>Cancelar</button><button type="button" className="btn btn--primary" onClick={() => void submitCuenta()} disabled={crearCuenta.isPending}>Crear cuenta</button></>
        }>
          <div className="modal-card__body">
            <div className="form-grid">
              <label className="field">
                <span className="field-label">Código *</span>
                <input
                  className="input"
                  value={cuentaForm.codigo}
                  onChange={(e) => {
                    const codigo = e.target.value;
                    const nivel = codigo.trim().split('.').filter(Boolean).length;
                    setCuentaForm({ ...cuentaForm, codigo, esDetalle: nivel === 4 });
                  }}
                  placeholder="5.1.11.001"
                />
                <span className="field-help">Niveles 1–3: título. Nivel 4: recibe movimientos.</span>
              </label>
              <label className="field"><span className="field-label">Nombre *</span><input className="input" value={cuentaForm.nombre} onChange={(e) => setCuentaForm({ ...cuentaForm, nombre: e.target.value })} /></label>
              <label className="field"><span className="field-label">Tipo</span><select className="input" value={cuentaForm.tipo} onChange={(e) => setCuentaForm({ ...cuentaForm, tipo: e.target.value })}><option value="ACTIVO">Activo</option><option value="PASIVO">Pasivo</option><option value="PATRIMONIO">Patrimonio</option><option value="INGRESO">Ingreso</option><option value="GASTO">Gasto</option><option value="COSTO">Costo</option></select></label>
              <label className="checkbox"><input type="checkbox" checked={nivelCuenta === 4} disabled onChange={() => undefined} />Cuenta de movimiento (nivel 4)</label>
            </div>
          </div>
        </Modal>
      ) : null}

      {selectedAsiento && detalle.data ? (
        <DetalleAsientoModal
          asiento={detalle.data}
          canAnnul={canEdit}
          onClose={() => setSelectedAsiento(null)}
          onAnnul={() => void annul(detalle.data.id, detalle.data.numero)}
        />
      ) : null}
      {claveAbierta ? (
        <Modal
          title="Clave de administrador"
          eyebrow="Numeración fiscal"
          onClose={() => {
            setClaveAbierta(false);
            setClaveAdmin('');
            setClaveError(null);
          }}
          footer={
            <>
              <button
                type="button"
                className="btn btn--outline"
                onClick={() => {
                  setClaveAbierta(false);
                  setClaveAdmin('');
                  setClaveError(null);
                }}
              >
                Cancelar
              </button>
              <button
                type="button"
                className="btn btn--primary"
                disabled={actualizarCorrelativo.isPending}
                onClick={() => void confirmarCambioControl()}
              >
                {actualizarCorrelativo.isPending ? 'Guardando…' : 'Confirmar cambio'}
              </button>
            </>
          }
        >
          <div className="modal-card__body">
            <p className="inline-note">
              Vas a iniciar el talonario en{' '}
              <strong>{controlFormateado('00', Number(siguienteControl) || 0)}</strong>. Esto
              afectará la próxima factura, nota de crédito, nota de débito y comprobante ISLR.
            </p>
            <label className="field">
              <span className="field-label">Clave de administrador *</span>
              <input
                className="input"
                type="password"
                autoFocus
                autoComplete="current-password"
                value={claveAdmin}
                onChange={(event) => {
                  setClaveAdmin(event.target.value);
                  setClaveError(null);
                }}
                onKeyDown={(event) => {
                  if (event.key === 'Enter') void confirmarCambioControl();
                }}
                placeholder="Escribe tu clave"
              />
            </label>
            {claveError ? <div className="form-error" role="alert">{claveError}</div> : null}
          </div>
        </Modal>
      ) : null}

      {periodoACerrar ? (
        <ConfirmModal
          title={`Cerrar período ${periodoACerrar.nombre}`}
          description="Al cerrar el período no se podrán registrar ni modificar asientos en ese rango de fechas."
          confirmLabel="Cerrar período"
          busy={cerrarPeriodo.isPending}
          onClose={() => setPeriodoACerrar(null)}
          onConfirm={async () => {
            try {
              await cerrarPeriodo.mutateAsync(periodoACerrar.id);
              notify(`Período ${periodoACerrar.nombre} cerrado.`, 'success');
              setPeriodoACerrar(null);
            } catch (error) {
              notify(error instanceof Error ? error.message : 'No se pudo cerrar.', 'error');
            }
          }}
        />
      ) : null}

      {annulAsiento ? (
        <MotivoModal
          title={`Anular asiento #${annulAsiento.numero}`}
          description="Se generará el asiento de reverso y el original quedará marcado como anulado."
          onClose={() => setAnnulAsiento(null)}
          onConfirm={confirmarAnnul}
        />
      ) : null}
    </div>
  );
}

function AsientoForm({
  cuentas,
  onClose,
  onCreated,
}: {
  cuentas: CuentaContable[];
  onClose: () => void;
  onCreated: (numero: number) => void;
}) {
  const { notify } = useToast();
  const crear = useCrearAsiento();
  const [fecha, setFecha] = useState(today);
  const [descripcion, setDescripcion] = useState('');
  const [tasa, setTasa] = useState('36.42');
  const [lineas, setLineas] = useState<LineaForm[]>([{ ...EMPTY_LINE }, { ...EMPTY_LINE }]);

  const totals = useMemo(() => {
    const debe = lineas.reduce((sum, line) => sum + (Number(line.debe) || 0), 0);
    const haber = lineas.reduce((sum, line) => sum + (Number(line.haber) || 0), 0);
    return {
      debe: Math.round(debe * 100) / 100,
      haber: Math.round(haber * 100) / 100,
      diff: Math.round((debe - haber) * 100) / 100,
    };
  }, [lineas]);

  const update = (index: number, patch: Partial<LineaForm>) =>
    setLineas((current) => current.map((line, i) => (i === index ? { ...line, ...patch } : line)));

  const submit = async () => {
    if (!descripcion.trim() || !fecha) {
      notify('Fecha y descripción son obligatorias.', 'warning');
      return;
    }
    if (Math.abs(totals.diff) > 0.009) {
      notify('El asiento debe estar cuadrado.', 'warning');
      return;
    }
    const payloadLines = lineas
      .filter((line) => line.cuentaId && (Number(line.debe) > 0 || Number(line.haber) > 0))
      .map((line) => ({
        cuentaId: line.cuentaId,
        descripcion: line.descripcion || descripcion,
        debeUsd: (Number(line.debe) || 0) / (Number(tasa) || 1),
        haberUsd: (Number(line.haber) || 0) / (Number(tasa) || 1),
      }));
    if (payloadLines.length < 2) {
      notify('Agrega al menos dos líneas.', 'warning');
      return;
    }
    try {
      const response = await crear.mutateAsync({ fecha, descripcion: descripcion.trim(), tasa: Number(tasa), lineas: payloadLines });
      onCreated(response.data.numero);
    } catch (error) {
      notify(error instanceof Error ? error.message : 'No se pudo crear el asiento.', 'error');
    }
  };

  return (
    <Modal title="Asiento manual" eyebrow="Partida doble" onClose={onClose} wide footer={
      <>
        <span className={`pill ${Math.abs(totals.diff) < 0.009 ? 'pill--soft' : 'pill--warning'}`}>
          Debe {formatMoney(totals.debe, 'VES')} · Haber {formatMoney(totals.haber, 'VES')}
        </span>
        <button type="button" className="btn btn--outline" onClick={onClose}>Cancelar</button>
        <button type="button" className="btn btn--primary" onClick={() => void submit()} disabled={crear.isPending}>Contabilizar</button>
      </>
    }>
      <div className="modal-card__body">
        <div className="form-grid form-grid--3">
          <label className="field"><span className="field-label">Fecha</span><input className="input" type="date" value={fecha} onChange={(e) => setFecha(e.target.value)} /></label>
          <label className="field field--wide"><span className="field-label">Descripción *</span><input className="input" value={descripcion} onChange={(e) => setDescripcion(e.target.value)} placeholder="Ajuste, reclasificación, gasto…" /></label>
          <label className="field"><span className="field-label">Tasa VES/USD</span><input className="input" value={tasa} onChange={(e) => setTasa(e.target.value)} /></label>
        </div>
        <div className="line-items">
          <div className="line-items__head"><strong>Líneas del asiento</strong><button type="button" className="btn btn--outline btn--sm" onClick={() => setLineas((c) => [...c, { ...EMPTY_LINE }])}><PlusIcon size={15} /> Línea</button></div>
          {lineas.map((line, index) => (
            <div className="item-row" key={index}>
              <SearchSelect
                value={line.cuentaId}
                onChange={(value) => update(index, { cuentaId: value })}
                options={cuentas.map((cuenta) => ({
                  value: cuenta.id,
                  label: cuenta.nombre,
                  hint: cuenta.codigo,
                }))}
                placeholder="Cuenta…"
                ariaLabel="Cuenta contable"
                className="item-row__product"
              />
              <input className="input item-row__price" placeholder="Descripción" value={line.descripcion} onChange={(e) => update(index, { descripcion: e.target.value })} />
              <input className="input item-row__qty" inputMode="decimal" placeholder="Debe Bs." value={line.debe} onChange={(e) => update(index, { debe: e.target.value, haber: '' })} />
              <input className="input item-row__qty" inputMode="decimal" placeholder="Haber Bs." value={line.haber} onChange={(e) => update(index, { haber: e.target.value, debe: '' })} />
              <button type="button" className="icon-button icon-button--tiny" aria-label="Eliminar línea" onClick={() => setLineas((c) => (c.length > 2 ? c.filter((_, i) => i !== index) : c))}><TrashIcon size={16} /></button>
            </div>
          ))}
        </div>
        <p className="page-footnote">El sistema valida cuentas detalle, período abierto y cuadre exacto en bolívares (respaldo en USD) antes de contabilizar.</p>
      </div>
    </Modal>
  );
}

function DetalleAsientoModal({
  asiento,
  canAnnul,
  onClose,
  onAnnul,
}: {
  asiento: AsientoDetalle;
  canAnnul: boolean;
  onClose: () => void;
  onAnnul: () => void;
}) {
  return (
    <Modal title={`Asiento #${asiento.numero}`} eyebrow={asiento.periodoNombre} onClose={onClose} wide footer={
      <>
        <button type="button" className="btn btn--outline" onClick={onClose}>Cerrar</button>
        {canAnnul && asiento.estado === 'CONTABILIZADO' ? <button type="button" className="btn btn--danger" onClick={onAnnul}>Anular con reverso</button> : null}
      </>
    }>
      <div className="modal-card__body">
        <div className="detail-grid">
          <div className="detail-block"><span>Fecha</span><strong>{formatDate(asiento.fecha)}</strong><small>{asiento.origen}</small></div>
          <div className="detail-block"><span>Estado</span><StatusBadge estado={asiento.estado} /><small>Tasa {formatRate(asiento.tasa)}</small></div>
          <div className="detail-block"><span>Descripción</span><strong>{asiento.descripcion}</strong><small>{asiento.periodoNombre}</small></div>
          <div className="detail-block"><span>Totales</span><strong>{formatMoney(asiento.totalDebeVes, 'VES')}</strong><small>Debe = Haber</small></div>
        </div>
        <div className="table-scroll">
          <table className="data-table data-table--compact">
            <thead><tr><th>Cuenta</th><th>Descripción</th><th className="align-right">Debe Bs.</th><th className="align-right">Haber Bs.</th></tr></thead>
            <tbody>
              {asiento.lineas.map((line) => (
                <tr key={line.id}>
                  <td><div className="doc-cell"><strong>{line.cuentaCodigo}</strong><span>{line.cuentaNombre}</span></div></td>
                  <td>{line.descripcion}</td>
                  <td className="align-right">{formatMoney(line.debeVes, 'VES')}</td>
                  <td className="align-right">{formatMoney(line.haberVes, 'VES')}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </Modal>
  );
}
